import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import PublicNav from "@/components/PublicNav";
import { ArrowLeft, Download } from "lucide-react";
import { ROLE_LABELS } from "@/lib/constants";
import { statusLabel, fmtDate, STATUS_COLOR } from "./admin-ops/constants";
import { DataTable, Pager } from "./admin-ops/DataTable";

const API = process.env.REACT_APP_BACKEND_URL;

const SECTIONS = {
  utilisateurs: { title: "Utilisateurs", subtitle: "Tous les comptes de la plateforme.", endpoint: "/admin/users", paged: false },
  candidats: { title: "Candidats", subtitle: "Profils candidats et situation de recrutement.", endpoint: "/admin-ops/candidates", paged: true },
  employeurs: { title: "Employeurs", subtitle: "Entreprises et informations de contact.", endpoint: "/admin-ops/employers", paged: true },
  offres: { title: "Offres actives", subtitle: "Offres d'emploi actuellement ouvertes.", endpoint: "/admin-ops/offers", fixedParams: { status: "active" }, paged: true },
  cvs: { title: "CVs téléchargeables", subtitle: "CV des candidats et fichiers téléversés.", endpoint: "/admin-ops/cvs", paged: true },
  candidatures: { title: "Candidatures", subtitle: "Toutes les candidatures avec historique complet.", endpoint: "/admin-ops/applications", paged: true },
};

const COLUMNS = {
  utilisateurs: [
    { key: "name", label: "Nom", render: (r) => <span className="font-semibold text-brand-navy">{r.name}</span> },
    { key: "email", label: "Courriel" },
    { key: "role", label: "Rôle", render: (r) => ROLE_LABELS[r.role] || r.role },
    { key: "nexa_id", label: "Identifiant NEXA", render: (r) => r.nexa_id || "—" },
    { key: "created_at", label: "Inscription", render: (r) => fmtDate(r.created_at) },
  ],
  candidats: [
    { key: "name", label: "Candidat", render: (r) => <span className="font-semibold text-brand-navy">{r.name}</span> },
    { key: "profession", label: "Profession" },
    { key: "country", label: "Pays" },
    { key: "experience_years", label: "Exp.", render: (r) => (r.experience_years ? `${r.experience_years} an(s)` : "—") },
    { key: "languages", label: "Langues", render: (r) => (r.languages || []).join(", ") || "—" },
    { key: "offer_title", label: "Offre concernée" },
    { key: "company", label: "Employeur" },
    { key: "commercial_status", label: "Statut", render: (r) => (
      <span className={`text-xs font-medium rounded-full px-2.5 py-1 ${STATUS_COLOR(r.commercial_status)}`}>{statusLabel(r.commercial_status, "candidate")}</span>
    ) },
    { key: "created_at", label: "Inscription", render: (r) => fmtDate(r.created_at) },
  ],
  employeurs: [
    { key: "company_name", label: "Entreprise", render: (r) => <span className="font-semibold text-brand-navy">{r.company_name}</span> },
    { key: "contact_name", label: "Contact" },
    { key: "email", label: "Courriel" },
    { key: "city", label: "Ville" },
    { key: "sector", label: "Secteur" },
    { key: "offers_count", label: "Offres" },
    { key: "commercial_status", label: "Statut commercial", render: (r) => (
      <span className={`text-xs font-medium rounded-full px-2.5 py-1 ${STATUS_COLOR(r.commercial_status)}`}>{statusLabel(r.commercial_status)}</span>
    ) },
  ],
  offres: [
    { key: "title", label: "Poste", render: (r) => <span className="font-semibold text-brand-navy">{r.title}</span> },
    { key: "company", label: "Employeur" },
    { key: "city", label: "Lieu", render: (r) => [r.city, r.country].filter(Boolean).join(", ") || "—" },
    { key: "sector", label: "Secteur" },
    { key: "candidates_distinct", label: "Candidats" },
    { key: "retained", label: "Retenus" },
    { key: "created_at", label: "Création", render: (r) => fmtDate(r.created_at) },
  ],
  cvs: [
    { key: "full_name", label: "Candidat", render: (r) => <span className="font-semibold text-brand-navy">{r.full_name}</span> },
    { key: "profession", label: "Profession" },
    { key: "country", label: "Pays / Ville", render: (r) => [r.country, r.city].filter(Boolean).join(" / ") || "—" },
    { key: "experience_years", label: "Exp.", render: (r) => (r.experience_years ? `${r.experience_years} an(s)` : "—") },
    { key: "owner_email", label: "Courriel" },
    { key: "files", label: "Fichiers téléchargeables", render: (r) => (
      r.files.length === 0
        ? <span data-testid={`cv-nofile-${r.cv_id}`} className="text-xs text-slate-400">Aucun fichier</span>
        : <div className="flex flex-col gap-1.5">
            {r.files.map((f) => (
              <a key={f.file_id} data-testid={`cv-download-${f.file_id}`} href={`${API}${f.download_url}`}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-blue border border-brand-blue/30 rounded-lg px-2.5 py-1.5 hover:bg-brand-sky hover:border-brand-turquoise transition-colors w-fit">
                <Download className="w-3.5 h-3.5" /> {f.name} ({Math.round((f.size || 0) / 1024)} Ko)
              </a>
            ))}
          </div>
    ) },
    { key: "created_at", label: "Création", render: (r) => fmtDate(r.created_at) },
  ],
  candidatures: [
    { key: "candidate_name", label: "Candidat", render: (r) => <span className="font-semibold text-brand-navy">{r.candidate_name}</span> },
    { key: "candidate_email", label: "Courriel" },
    { key: "offer_title", label: "Offre" },
    { key: "company", label: "Employeur" },
    { key: "status", label: "Statut", render: (r) => (
      <span className={`text-xs font-medium rounded-full px-2.5 py-1 ${STATUS_COLOR(r.status)}`}>{statusLabel(r.status, "candidate")}</span>
    ) },
    { key: "history", label: "Historique", render: (r) => (
      <span data-testid={`candidature-history-${r.application_id}`} className="text-xs text-slate-500">
        {(r.history || []).length === 0 ? "—" : r.history.map((h) => `${statusLabel(h.status, "candidate")} (${fmtDate(h.at)})`).join(" → ")}
      </span>
    ) },
    { key: "created_at", label: "Déposée le", render: (r) => fmtDate(r.created_at) },
  ],
};

const ROW_KEYS = {
  utilisateurs: "user_id", candidats: "candidate_id", employeurs: "employer_id",
  offres: "offer_id", cvs: "cv_id", candidatures: "application_id",
};

const EXPORT_TYPES = {
  employeurs: "employers", candidats: "candidates", offres: "offers",
  cvs: "cvs", candidatures: "applications",
};

const SEARCH_PLACEHOLDERS = {
  utilisateurs: "Nom ou courriel…", candidats: "Nom, courriel, identifiant…",
  employeurs: "Entreprise, contact, courriel…", offres: "Titre, employeur, ville…",
  cvs: "Nom, profession, pays…", candidatures: "Candidat, offre, employeur…",
};

export default function AdminLists() {
  const { type } = useParams();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const section = SECTIONS[type];
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [error, setError] = useState("");

  const allowed = user && user.role === "super_admin";

  useEffect(() => {
    if (!authLoading && !user) navigate("/login", { replace: true });
    else if (!authLoading && user && user.role !== "super_admin") navigate("/dashboard", { replace: true });
  }, [user, authLoading, navigate]);

  useEffect(() => { setQ(""); setPage(1); setItems([]); }, [type]);

  const load = useCallback(async () => {
    if (!section) return;
    const params = new URLSearchParams();
    if (section.paged) { params.set("page", String(page)); params.set("limit", "25"); }
    if (q) params.set("q", q);
    Object.entries(section.fixedParams || {}).forEach(([k, v]) => params.set(k, v));
    const res = await api.get(`${section.endpoint}?${params}`);
    if (Array.isArray(res.data)) {
      setItems(res.data);
      setTotal(res.data.length);
    } else {
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    }
  }, [section, page, q]);

  useEffect(() => {
    if (!allowed || !section) return;
    const t = setTimeout(() => load().catch((e) => setError(formatApiError(e))), 250);
    return () => clearTimeout(t);
  }, [allowed, section, load]);

  if (!user || !allowed) return null;

  if (!section) {
    return (
      <div className="min-h-screen bg-slate-50">
        <PublicNav />
        <div className="max-w-4xl mx-auto px-6 py-16 text-center">
          <p data-testid="admin-lists-unknown" className="text-slate-500">Liste inconnue.</p>
          <Link to="/dashboard" className="text-brand-blue text-sm font-medium">Retour au tableau de bord</Link>
        </div>
      </div>
    );
  }

  const pages = Math.max(1, Math.ceil(total / 25));
  return (
    <div data-testid="admin-lists-page" className="min-h-screen bg-slate-50">
      <PublicNav />
      <div className="bg-brand-navy">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <Link data-testid="admin-lists-back" to="/dashboard"
            className="inline-flex items-center gap-1.5 text-white/70 hover:text-white text-xs font-semibold mb-3">
            <ArrowLeft className="w-3.5 h-3.5" /> Tableau de bord
          </Link>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-white">{section.title}</h1>
          <p className="text-white/70 text-sm mt-1">{section.subtitle}</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8 space-y-4">
        {error && <div data-testid="admin-lists-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3">{error}</div>}
        <div className="flex flex-wrap items-center gap-2">
          <input data-testid={`list-${type}-search`} value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }}
            placeholder={SEARCH_PLACEHOLDERS[type]}
            className="w-full max-w-md text-sm border border-slate-200 rounded-lg px-3.5 py-2.5 bg-white" />
          {EXPORT_TYPES[type] && (
            <a data-testid={`list-${type}-export`} href={`${API}/api/admin-ops/export?type=${EXPORT_TYPES[type]}`}
              className="flex items-center gap-1.5 text-xs font-semibold text-brand-navy border border-slate-200 rounded-lg px-3.5 py-2.5 bg-white hover:border-brand-turquoise transition-colors">
              <Download className="w-3.5 h-3.5" /> Exporter CSV
            </a>
          )}
          {type === "cvs" && (
            <span data-testid="cvs-files-summary" className="text-xs text-slate-500">
              {total} CV au registre — {items.reduce((n, r) => n + (r.files?.length || 0), 0)} fichier(s) téléchargeable(s) sur cette page
            </span>
          )}
        </div>
        <DataTable testid={`list-${type}-table`} rowKey={ROW_KEYS[type]}
          empty="Aucun résultat." columns={COLUMNS[type]} rows={items} />
        {section.paged && (
          <Pager testid={`list-${type}-pager`} page={page} pages={pages} total={total}
            onPage={(p) => setPage(Math.min(Math.max(1, p), pages))} />
        )}
      </div>
    </div>
  );
}
